// src/hooks/useStartSyncWatcher.js
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  startLocalStorageSyncWatcher,
  stopLocalStorageSyncWatcher,
} from "../redux/slices/lsSyncWatcherSlice";

/**
 * Starts the LS sync watcher only when the user is authenticated.
 * Auto-stops it on logout, 401 redirects, or when visiting /login.
 *
 * NOTE: Works cleanly with React 19 StrictMode (dev) because we
 * also dispatch a stop on cleanup to avoid duplicate listeners.
 */
export default function useStartSyncWatcher(throttleMs = 3000) {
  const dispatch = useDispatch();

  // Pull your auth state from Redux (adjust selectors to your slice shape)
  const authSlice = useSelector((s) => s.auth);
  const isAuthedByState =
    !!authSlice?.isAuthenticated ||
    !!authSlice?.token ||
    !!authSlice?.accessToken;

  // Double-check localStorage in case Redux state is stale for a moment
  const hasToken = useMemo(() => {
    try {
      return !!localStorage.getItem("authToken");
    } catch {
      return false;
    }
  }, []);

  // Don’t run the watcher on the login screen
  const onLoginRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/login");

  useEffect(() => {
    // If not authenticated (or on login), make sure watcher is stopped and bail out
    if (!isAuthedByState || !hasToken || onLoginRoute) {
      dispatch(stopLocalStorageSyncWatcher());
      return;
    }

    // Start watcher when authenticated
    dispatch(startLocalStorageSyncWatcher({ throttleMs, reason: "app-mount" }));

    // Cleanup on unmount or when auth flips to false
    return () => {
      dispatch(stopLocalStorageSyncWatcher());
    };
  }, [dispatch, throttleMs, isAuthedByState, hasToken, onLoginRoute]);
}
